"use client";

import { useEffect, useRef, useState } from "react";

const stats = [
  {
    end: 500,
    suffix: "+",
    label: "Active Members",
    sublabel: "and growing every month",
    icon: "👥",
    color: "var(--terracotta)",
  },
  {
    end: 2400,
    suffix: "+",
    label: "Coaching Hours",
    sublabel: "delivered by certified coaches",
    icon: "⏱",
    color: "#7eb8f7",
  },
  {
    end: 12,
    suffix: "",
    label: "Tournaments Hosted",
    sublabel: "local & regional level",
    icon: "🏆",
    color: "#f7c97e",
  },
  {
    end: 8,
    suffix: "",
    label: "Performance Programs",
    sublabel: "across all disciplines",
    icon: "📈",
    color: "#7ef7b8",
  },
];

function useCounter(end: number, duration: number = 2000, start: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration, start]);

  return count;
}

function StatCard({ stat, index, active }: { stat: typeof stats[0]; index: number; active: boolean }) {
  const count = useCounter(stat.end, 2200, active);

  return (
    <div
      className={`stat-card-shine glass-card relative p-8 text-center`}
      style={{ overflow: "hidden" }}
    >
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none opacity-10"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${stat.color} 0%, transparent 70%)`,
        }}
      />

      <div className="relative z-10">
        <div className="text-4xl mb-4">{stat.icon}</div>
        <div
          className="text-5xl md:text-6xl font-bold mb-2"
          style={{ color: stat.color, fontVariantNumeric: "tabular-nums" }}
        >
          {count.toLocaleString()}{stat.suffix}
        </div>
        <div className="text-white font-bold text-lg mb-1">{stat.label}</div>
        <div className="text-white/40 text-xs font-medium uppercase tracking-wide">{stat.sublabel}</div>
      </div>
    </div>
  );
}

export default function StatsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState(false);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(true);
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    cardRefs.current.forEach((card) => { if (card) observer.observe(card); });

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
      cardRefs.current.forEach((card) => { if (card) observer.unobserve(card); });
    };
  }, []);

  return (
    <section
      id="stats"
      ref={sectionRef}
      className="relative py-16 sm:py-20 md:py-28 overflow-hidden"
      style={{ background: "var(--navy-dark)" }}
    >
      {/* Floating background shapes */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(224,96,49,0.04) 0%, transparent 70%)",
        }}
      />
      <div
        className="absolute top-10 right-20 w-48 h-48 rounded-full pointer-events-none"
        style={{
          border: "1px solid rgba(224,96,49,0.08)",
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="reveal text-center mb-16" ref={(el) => { if (el) { const obs = new IntersectionObserver((entries) => { entries.forEach(e => { if (e.isIntersecting) { el.classList.add('visible'); obs.unobserve(el); } }); }, { threshold: 0.1 }); obs.observe(el); } }}>
          <p className="section-label mb-4">By the Numbers</p>
          <h2 className="headline-lg text-white">
            Results That{" "}
            <span className="gradient-text">Speak.</span>
          </h2>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              ref={(el) => { cardRefs.current[i] = el; }}
              className={`reveal stagger-${i + 1}`}
            >
              <StatCard stat={stat} index={i} active={active} />
            </div>
          ))}
        </div>

        {/* Bottom tagline */}
        <div className="text-center mt-16">
          <p className="text-white/40 text-sm font-medium">
            Join a community built on{" "}
            <span style={{ color: "var(--terracotta)" }}>discipline</span>,{" "}
            <span style={{ color: "var(--terracotta)" }}>performance</span>, and{" "}
            <span style={{ color: "var(--terracotta)" }}>community</span>.
          </p>
        </div>
      </div>
    </section>
  );
}